import { InlineDialog } from 'components/dialogs/Dialog'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import {
  BreakoutRadioButton,
  BreakoutRadioGroup,
} from 'components/design-system/BreakoutRadioButton'
import { BreakoutTextArea } from 'components/design-system/BreakoutTextArea'
import { useTranslationTyped } from 'i18n/i18n'
import { useState, useMemo, useId } from 'react'
import classNames from 'classnames'

const NaReasonTypes = {
  PEER_DID_NOT_ATTEND: 'peer_did_not_attend',
  I_DID_NOT_ATTEND: 'i_did_not_attend',
  OTHER: 'other',
} as const
type NaReasonTypes = (typeof NaReasonTypes)[keyof typeof NaReasonTypes]

export function RequireReasonForNADialog({
  open,
  revieweeName,
  existingNote,
  onConfirm,
  onCancel,
  isSelfReview,
}: {
  open: boolean
  revieweeName: string
  existingNote?: string
  onConfirm: (reason: string) => void
  onCancel: () => void
  isSelfReview: boolean
}) {
  const { tt } = useTranslationTyped()
  const labelId = useId()
  const [selectedReason, setSelectedReason] = useState<NaReasonTypes>(() => {
    const trimmedNote = existingNote?.trim() || ''
    if (trimmedNote !== '') {
      if (
        trimmedNote ===
          tt.student_assignment.peer_review_dialog.require_reason_for_na.reason_peer_did_not_attend() &&
        !isSelfReview
      ) {
        return NaReasonTypes.PEER_DID_NOT_ATTEND
      }
      if (
        trimmedNote ===
        tt.student_assignment.peer_review_dialog.require_reason_for_na.reason_i_did_not_attend()
      ) {
        return NaReasonTypes.I_DID_NOT_ATTEND
      }
      return NaReasonTypes.OTHER
    }
    return isSelfReview
      ? NaReasonTypes.I_DID_NOT_ATTEND
      : NaReasonTypes.PEER_DID_NOT_ATTEND
  })
  const [customReason, setCustomReason] = useState(existingNote || '')

  const finalReason = useMemo(() => {
    if (selectedReason === NaReasonTypes.OTHER) {
      return customReason.trim()
    }
    if (selectedReason === NaReasonTypes.PEER_DID_NOT_ATTEND && !isSelfReview) {
      return tt.student_assignment.peer_review_dialog.require_reason_for_na.reason_peer_did_not_attend()
    }
    if (selectedReason === NaReasonTypes.I_DID_NOT_ATTEND) {
      return tt.student_assignment.peer_review_dialog.require_reason_for_na.reason_i_did_not_attend()
    }
    return ''
  }, [
    selectedReason,
    isSelfReview,
    customReason,
    tt.student_assignment.peer_review_dialog.require_reason_for_na,
  ])

  const canConfirm = useMemo(() => {
    if (selectedReason === NaReasonTypes.OTHER) {
      return customReason.trim().length > 0
    }
    return true
  }, [selectedReason, customReason])

  const handleConfirm = () => {
    if (canConfirm) {
      onConfirm(finalReason)
    }
  }

  return (
    <InlineDialog
      aria-labelledby={undefined}
      open={open}
      size="sm"
      innerClassName="flex flex-col gap-3 p-[52px] min-h-[min(400px,90vh)]"
      className="isolate !bg-surface-bright"
      onDismiss={onCancel}
    >
      <DialogCloseButton
        onClick={() => {
          onCancel()
          return false
        }}
      />
      <div className="flex w-full max-w-[450px] flex-1 flex-col items-start gap-3">
        <div className="flex w-full flex-col items-center gap-3">
          <div className="flex w-full flex-col items-center gap-3">
            <h2
              className="w-full text-center text-headline-large text-on-surface"
              id={`${labelId}-title`}
            >
              {tt.student_assignment.peer_review_dialog.require_reason_for_na.title()}
            </h2>
            <p className="w-full text-center text-body-medium text-on-surface-var">
              {tt.student_assignment.peer_review_dialog.require_reason_for_na.description(
                { name: revieweeName }
              )}
            </p>
            <BreakoutRadioGroup
              required
              aria-labelledby={`${labelId}-title`}
              value={selectedReason}
              onValueChange={(val) => {
                // Radix (for good reason) does not support generic value types.
                // The field should probably be refactored to be a string, but
                // limiting the scope of this refactor for now -- we cast
                setSelectedReason(val as NaReasonTypes)
              }}
              className="flex w-full flex-col items-start gap-1"
            >
              {!isSelfReview && (
                <div className="flex w-full cursor-pointer items-center gap-2">
                  <BreakoutRadioButton
                    variant="bare"
                    value={NaReasonTypes.PEER_DID_NOT_ATTEND}
                    label={null}
                    aria-labelledby={`${labelId}-peer-did-not-attend`}
                  />
                  <div
                    className="flex min-w-0 flex-1 flex-col gap-1"
                    id={`${labelId}-peer-did-not-attend`}
                  >
                    <div className="flex items-center gap-1 py-0.5">
                      <p className="whitespace-nowrap text-body-medium text-on-surface-var">
                        {tt.student_assignment.peer_review_dialog.require_reason_for_na.reason_peer_did_not_attend()}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex w-full cursor-pointer items-center gap-2">
                <BreakoutRadioButton
                  variant="bare"
                  value={NaReasonTypes.I_DID_NOT_ATTEND}
                  label={null}
                  aria-labelledby={`${labelId}-i-did-not-attend`}
                />
                <div
                  className="flex min-w-0 flex-1 flex-col gap-1"
                  id={`${labelId}-i-did-not-attend`}
                >
                  <div className="flex items-center gap-1 py-0.5">
                    <p className="whitespace-nowrap text-body-medium text-on-surface-var">
                      {tt.student_assignment.peer_review_dialog.require_reason_for_na.reason_i_did_not_attend()}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex w-full cursor-pointer items-center gap-2">
                <BreakoutRadioButton
                  variant="bare"
                  value={NaReasonTypes.OTHER}
                  label={null}
                  aria-labelledby={`${labelId}-other`}
                />
                <div
                  className="flex min-w-0 flex-1 flex-col gap-1"
                  id={`${labelId}-other`}
                >
                  <div className="flex items-center gap-1 py-0.5">
                    <p className="text-body-medium text-on-surface-var">
                      {tt.student_assignment.peer_review_dialog.require_reason_for_na.reason_other()}
                    </p>
                  </div>
                </div>
              </div>
            </BreakoutRadioGroup>
            {selectedReason === NaReasonTypes.OTHER && (
              <div className="flex w-full flex-col items-start gap-1">
                <BreakoutTextArea
                  value={customReason}
                  onInput={(e) => setCustomReason(e.currentTarget.value)}
                  placeholder={tt.student_assignment.peer_review_dialog.require_reason_for_na.reason_placeholder()}
                  autoGrow
                  inputClassName="!min-h-[52px] max-h-[200px] resize-none"
                  containerClassName="w-full"
                  autoFocusWithDelay={100}
                  maxLength={100}
                  hideCharacterCount
                  aria-label={tt.student_assignment.peer_review_dialog.require_reason_for_na.reason_placeholder()}
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex-1" />
        <div className="flex w-full items-start justify-center gap-3">
          <BreakoutButton
            kind="secondary"
            size="medium"
            onClick={onCancel}
            className="flex-1"
          >
            {tt.student_assignment.peer_review_dialog.require_reason_for_na.cancel()}
          </BreakoutButton>
          <BreakoutButton
            kind="accent"
            size="medium"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className={classNames('flex-1', !canConfirm && 'opacity-30')}
          >
            {tt.student_assignment.peer_review_dialog.require_reason_for_na.confirm()}
          </BreakoutButton>
        </div>
      </div>
    </InlineDialog>
  )
}
